// IndexedDB utility for offline storage

export type OfflineSyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface OfflineOrder {
  id: string;
  orderNumber: string;
  items: any[];
  total: number;
  customerName: string;
  customerPhone?: string;
  customerId?: string;
  orderType: 'dine-in' | 'takeaway' | 'delivery';
  tableNumber?: string;
  tableId?: string | null;
  notes?: string;
  createdAt: string;
  synced: boolean;
  restaurantId: string;
  loyalty_pending?: boolean;

  // ===== Offline sync hardening fields =====
  // PIN-mode attribution (so syncing works without a Supabase auth.users session)
  staffProfileId?: string;
  deviceId?: string;
  isPINMode?: boolean;

  // Sync lifecycle
  syncStatus?: OfflineSyncStatus;
  syncError?: string;
  /** Automatic sync attempts that failed (reset by a manual Sync Now). */
  syncAttempts?: number;
  /** True once automatic retrying stopped (10 attempts or 1 hour). */
  syncGaveUp?: boolean;
  /** When the online attempt that led to this queue save was made. */
  originalAttemptAt?: string;
  syncedAt?: string;
  realOrderNumber?: string;
  realOrderId?: string;

  // Cached financials so we can print + replay precisely
  subtotal?: number;
  serviceCharge?: number;
  taxAmount?: number;
  tipAmount?: number;
  corporateDiscountAmount?: number;
  loyaltyDiscountAmount?: number;
  stampDiscountAmount?: number;
  manualDiscountAmount?: number;
  paymentMethod?: 'card' | 'cash' | 'cod';
  paymentStatus?: 'paid' | 'unpaid' | 'pending';
  status?: 'pending' | 'preparing' | 'ready' | 'completed' | 'cancelled';
  deliveryProvider?: string;

  // Snapshot of fields needed by pos-create-order on replay
  orderDataSnapshot?: any;
  orderItemsSnapshot?: any[];
}

export interface CachedMenuItem {
  id: string;
  name: string;
  nameAr: string;
  price: number;
  category: string;
  available: boolean;
  modifiers?: any[];
  image?: string;
  // Fix 3a: cached image blob so menu items render offline without network
  imageBlob?: Blob;
  imageMime?: string;
  restaurantId: string;
  cachedAt: string;
}

export interface CachedRestaurantSettings {
  id: string;
  name: string;
  taxRate: number;
  serviceCharge: number;
  currency: string;
  cachedAt: string;
}

export interface CachedFloorPlan {
  id: string; // restaurantId
  tables: any[];
  sections: any[];
  layouts: any[];
  cachedAt: string;
}

export interface CachedMenuCategory {
  id: string;
  restaurantId: string;
  name: string;
  nameAr?: string | null;
  sortOrder: number;
  isDefault: boolean;
  cachedAt: string;
}

export interface CachedCustomer {
  id: string;
  restaurantId: string;
  data: any; // full mapped customer record (frontend shape)
  cachedAt: string;
}

export interface PinCacheEntry {
  id: string; // `${restaurantId}:${staffProfileId}`
  restaurantId: string;
  staffProfileId: string;
  staffName: string;
  role: string;
  pinHash: string;
  cachedAt: string; // ISO
}


const OFFLINE_COUNTER_KEY = 'offlineOrderCounter';

class IndexedDBManager {
  // NOTE: dbName kept identical; bumped version to add new object stores.
  private dbName = 'BRQPOSOfflineDB';
  private version = 4;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    try {
      if (!('indexedDB' in window)) {
        throw new Error('IndexedDB is not supported in this browser');
      }

      return new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, this.version);

        request.onerror = () => {
          console.error('IndexedDB initialization error:', request.error);
          reject(request.error || new Error('Failed to open IndexedDB'));
        };

        request.onsuccess = () => {
          this.db = request.result;
          resolve();
        };

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;

          if (!db.objectStoreNames.contains('offlineOrders')) {
            const ordersStore = db.createObjectStore('offlineOrders', { keyPath: 'id' });
            ordersStore.createIndex('synced', 'synced', { unique: false });
            ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
          }

          if (!db.objectStoreNames.contains('menuItems')) {
            const menuStore = db.createObjectStore('menuItems', { keyPath: 'id' });
            menuStore.createIndex('restaurantId', 'restaurantId', { unique: false });
            menuStore.createIndex('category', 'category', { unique: false });
          }

          if (!db.objectStoreNames.contains('restaurantSettings')) {
            db.createObjectStore('restaurantSettings', { keyPath: 'id' });
          }

          if (!db.objectStoreNames.contains('authTokens')) {
            db.createObjectStore('authTokens', { keyPath: 'id' });
          }

          // v2 — PIN auth cache (offline fallback)
          if (!db.objectStoreNames.contains('pinCache')) {
            const pinStore = db.createObjectStore('pinCache', { keyPath: 'id' });
            pinStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          }

          // v2 — Per-device sequential counter for OFFLINE-NNNN order numbers
          if (!db.objectStoreNames.contains('offlineMeta')) {
            db.createObjectStore('offlineMeta', { keyPath: 'key' });
          }

          // v3 — Floor plan cache (tables + sections + layouts per restaurant)
          if (!db.objectStoreNames.contains('floorPlan')) {
            db.createObjectStore('floorPlan', { keyPath: 'id' });
          }

          // v4 — Menu categories cache
          if (!db.objectStoreNames.contains('menuCategories')) {
            const catStore = db.createObjectStore('menuCategories', { keyPath: 'id' });
            catStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          }

          // v4 — Customers cache (offline search + add)
          if (!db.objectStoreNames.contains('customers')) {
            const custStore = db.createObjectStore('customers', { keyPath: 'id' });
            custStore.createIndex('restaurantId', 'restaurantId', { unique: false });
          }
        };
      });
    } catch (error) {
      console.error('IndexedDB initialization failed:', error);
      throw error;
    }
  }


  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    return this.db!;
  }

  // ===== Offline Orders =====
  async saveOfflineOrder(order: OfflineOrder): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['offlineOrders'], 'readwrite');
    const store = transaction.objectStore('offlineOrders');

    return new Promise((resolve, reject) => {
      const request = store.put(order);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineOrders(): Promise<OfflineOrder[]> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['offlineOrders'], 'readonly');
    const store = transaction.objectStore('offlineOrders');

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getUnsyncedOrders(): Promise<OfflineOrder[]> {
    const all = await this.getOfflineOrders();
    return all.filter(o => !o.synced);
  }

  async updateOfflineOrder(orderId: string, patch: Partial<OfflineOrder>): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['offlineOrders'], 'readwrite');
    const store = transaction.objectStore('offlineOrders');

    return new Promise((resolve, reject) => {
      const getRequest = store.get(orderId);
      getRequest.onsuccess = () => {
        const existing = getRequest.result;
        if (!existing) return resolve();
        const merged = { ...existing, ...patch };
        const putRequest = store.put(merged);
        putRequest.onsuccess = () => resolve();
        putRequest.onerror = () => reject(putRequest.error);
      };
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async markOrderAsSynced(orderId: string, patch: Partial<OfflineOrder> = {}): Promise<void> {
    await this.updateOfflineOrder(orderId, {
      synced: true,
      syncStatus: 'synced',
      syncedAt: new Date().toISOString(),
      ...patch,
    });
  }

  async deleteOfflineOrder(orderId: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['offlineOrders'], 'readwrite');
    const store = transaction.objectStore('offlineOrders');

    return new Promise((resolve, reject) => {
      const request = store.delete(orderId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ===== Offline order number counter =====
  async getNextOfflineCounter(): Promise<number> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['offlineMeta'], 'readwrite');
    const store = transaction.objectStore('offlineMeta');

    return new Promise((resolve, reject) => {
      const getReq = store.get(OFFLINE_COUNTER_KEY);
      getReq.onsuccess = () => {
        const current = (getReq.result?.value as number) || 0;
        const next = current + 1;
        const putReq = store.put({ key: OFFLINE_COUNTER_KEY, value: next });
        putReq.onsuccess = () => resolve(next);
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }

  async resetOfflineCounter(): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['offlineMeta'], 'readwrite');
    const store = transaction.objectStore('offlineMeta');
    return new Promise((resolve, reject) => {
      const req = store.put({ key: OFFLINE_COUNTER_KEY, value: 0 });
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // ===== Menu Items cache =====
  async cacheMenuItems(items: CachedMenuItem[]): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['menuItems'], 'readwrite');
    const store = transaction.objectStore('menuItems');

    return new Promise((resolve, reject) => {
      let completed = 0;
      const total = items.length;

      if (total === 0) {
        resolve();
        return;
      }

      items.forEach(item => {
        const request = store.put(item);
        request.onsuccess = () => {
          completed++;
          if (completed === total) resolve();
        };
        request.onerror = () => reject(request.error);
      });
    });
  }

  async getCachedMenuItems(restaurantId: string): Promise<CachedMenuItem[]> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['menuItems'], 'readonly');
    const store = transaction.objectStore('menuItems');
    const index = store.index('restaurantId');

    return new Promise((resolve, reject) => {
      const request = index.getAll(restaurantId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ===== Restaurant Settings cache =====
  async cacheRestaurantSettings(settings: CachedRestaurantSettings): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['restaurantSettings'], 'readwrite');
    const store = transaction.objectStore('restaurantSettings');

    return new Promise((resolve, reject) => {
      const request = store.put(settings);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedRestaurantSettings(restaurantId: string): Promise<CachedRestaurantSettings | null> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['restaurantSettings'], 'readonly');
    const store = transaction.objectStore('restaurantSettings');

    return new Promise((resolve, reject) => {
      const request = store.get(restaurantId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  // ===== Auth tokens =====
  async saveAuthToken(token: { id: string; token: string; expiresAt: string }): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['authTokens'], 'readwrite');
    const store = transaction.objectStore('authTokens');

    return new Promise((resolve, reject) => {
      const request = store.put(token);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getAuthToken(id: string): Promise<any> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['authTokens'], 'readonly');
    const store = transaction.objectStore('authTokens');

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ===== PIN cache (offline auth fallback) =====
  async savePinCache(entry: PinCacheEntry): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['pinCache'], 'readwrite');
    const store = transaction.objectStore('pinCache');
    return new Promise((resolve, reject) => {
      const req = store.put(entry);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async getPinCacheByRestaurant(restaurantId: string): Promise<PinCacheEntry[]> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['pinCache'], 'readonly');
    const store = transaction.objectStore('pinCache');
    const index = store.index('restaurantId');
    return new Promise((resolve, reject) => {
      const req = index.getAll(restaurantId);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  async deletePinCacheEntry(id: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['pinCache'], 'readwrite');
    const store = transaction.objectStore('pinCache');
    return new Promise((resolve, reject) => {
      const req = store.delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // ===== Floor plan cache (Fix 3b) =====
  async cacheFloorPlan(plan: CachedFloorPlan): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['floorPlan'], 'readwrite');
    const store = transaction.objectStore('floorPlan');
    return new Promise((resolve, reject) => {
      const req = store.put(plan);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async getCachedFloorPlan(restaurantId: string): Promise<CachedFloorPlan | null> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['floorPlan'], 'readonly');
    const store = transaction.objectStore('floorPlan');
    return new Promise((resolve, reject) => {
      const req = store.get(restaurantId);
      req.onsuccess = () => resolve((req.result as CachedFloorPlan) || null);
      req.onerror = () => reject(req.error);
    });
  }

  // ===== Menu categories cache =====
  async cacheMenuCategories(
    restaurantId: string,
    categories: Array<Omit<CachedMenuCategory, 'cachedAt' | 'restaurantId'>>,
  ): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['menuCategories'], 'readwrite');
    const store = transaction.objectStore('menuCategories');
    return new Promise((resolve, reject) => {
      const index = store.index('restaurantId');
      const getReq = index.getAll(restaurantId);
      getReq.onsuccess = () => {
        const existing = (getReq.result as any[]) || [];
        existing.forEach((e: any) => store.delete(e.id));
        if (categories.length === 0) return resolve();
        let completed = 0;
        categories.forEach((cat) => {
          const entry: CachedMenuCategory = {
            ...cat,
            restaurantId,
            cachedAt: new Date().toISOString(),
          };
          const req = store.put(entry);
          req.onsuccess = () => {
            completed++;
            if (completed === categories.length) resolve();
          };
          req.onerror = () => reject(req.error);
        });
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }

  async getCachedMenuCategories(restaurantId: string): Promise<CachedMenuCategory[]> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['menuCategories'], 'readonly');
    const store = transaction.objectStore('menuCategories');
    const index = store.index('restaurantId');
    return new Promise((resolve, reject) => {
      const req = index.getAll(restaurantId);
      req.onsuccess = () => resolve((req.result as CachedMenuCategory[]) || []);
      req.onerror = () => reject(req.error);
    });
  }

  // ===== Customers cache =====
  async cacheCustomers(restaurantId: string, customers: any[]): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['customers'], 'readwrite');
    const store = transaction.objectStore('customers');
    return new Promise((resolve, reject) => {
      const index = store.index('restaurantId');
      const getReq = index.getAll(restaurantId);
      getReq.onsuccess = () => {
        const existing = (getReq.result as any[]) || [];
        existing.forEach((e: any) => store.delete(e.id));
        if (customers.length === 0) return resolve();
        let completed = 0;
        customers.forEach((c) => {
          const entry: CachedCustomer = {
            id: c.id,
            restaurantId,
            data: c,
            cachedAt: new Date().toISOString(),
          };
          const req = store.put(entry);
          req.onsuccess = () => {
            completed++;
            if (completed === customers.length) resolve();
          };
          req.onerror = () => reject(req.error);
        });
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }

  async addCachedCustomer(restaurantId: string, customer: any): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['customers'], 'readwrite');
    const store = transaction.objectStore('customers');
    return new Promise((resolve, reject) => {
      const entry: CachedCustomer = {
        id: customer.id,
        restaurantId,
        data: customer,
        cachedAt: new Date().toISOString(),
      };
      const req = store.put(entry);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  async getCachedCustomers(restaurantId: string): Promise<any[]> {
    const db = await this.ensureDB();
    const transaction = db.transaction(['customers'], 'readonly');
    const store = transaction.objectStore('customers');
    const index = store.index('restaurantId');
    return new Promise((resolve, reject) => {
      const req = index.getAll(restaurantId);
      req.onsuccess = () => {
        const rows = (req.result as CachedCustomer[]) || [];
        resolve(rows.map((r) => r.data));
      };
      req.onerror = () => reject(req.error);
    });
  }

  // Clear all data (for logout)

  async clearAllData(): Promise<void> {
    const db = await this.ensureDB();
    const storeNames = ['offlineOrders', 'menuItems', 'restaurantSettings', 'authTokens'];

    const transaction = db.transaction(storeNames, 'readwrite');

    return new Promise((resolve, reject) => {
      let completed = 0;
      const total = storeNames.length;

      storeNames.forEach(storeName => {
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => {
          completed++;
          if (completed === total) resolve();
        };
        request.onerror = () => reject(request.error);
      });
    });
  }
}

export const indexedDBManager = new IndexedDBManager();

// ===== Helpers =====

export function formatOfflineOrderNumber(counter: number): string {
  return `OFFLINE-${String(counter).padStart(4, '0')}`;
}

export async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function hashPin(pin: string, staffProfileId: string): Promise<string> {
  // Salt with staff profile id so the same PIN across staff doesn't collide.
  return sha256Hex(`${pin}:${staffProfileId}`);
}
